/**
 * Belegdesign: das PDF-Layout je Belegart anpassen.
 *
 * Links das Formular, rechts eine PDF-Vorschau mit Beispieldaten, die bei
 * jeder Änderung neu gerendert wird. Die Pflichtblöcke (Anschrift,
 * Belegdaten, Tabelle, Summen, Fußzeile) lassen sich verschieben und
 * umgestalten, aber nicht entfernen - das erzwingt schon das
 * Vorlagenmodell auf dem Server.
 */

import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';

import {
  DOCUMENT_TYPES,
  TEMPLATE_FIELDS,
  type DocTemplate,
  type DocTemplateExtraText,
  type DocumentType,
} from '@erp/shared';

import { api, ApiError, apiUrl } from '../api/client';
import { useAuth } from '../auth';
import { useToast } from '../components/toast';
import { Alert, Field, LoadingBlock, Select } from '../components/ui';

const TYPE_OPTIONS = Object.values(DOCUMENT_TYPES).map((meta) => ({
  value: meta.type,
  label: meta.label,
}));

export function TemplateDesignPage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { can } = useAuth();
  const readOnly = !can('settings:write');

  const [docType, setDocType] = useState<DocumentType>('invoice');
  const [tpl, setTpl] = useState<DocTemplate | null>(null);
  const [isCustom, setIsCustom] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { data: loaded, isLoading } = useQuery({
    queryKey: ['doc-template', docType],
    queryFn: () => api.get<{ template: DocTemplate; isCustom: boolean }>(`/api/settings/templates/${docType}`),
  });
  // Vorlage aus den Daten uebernehmen, nicht in der Ladefunktion: Aus dem
  // Zwischenspeicher laeuft die Ladefunktion nicht.
  useEffect(() => {
    if (!loaded) return;
    setTpl(loaded.template);
    setIsCustom(loaded.isCustom);
    setErrors({});
  }, [loaded]);

  // --- Vorschau --------------------------------------------------------------
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const previewUrlRef = useRef<string | null>(null);

  useEffect(() => {
    if (!tpl) return;

    const handle = setTimeout(async () => {
      try {
        const response = await fetch(apiUrl('/api/settings/templates/preview'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ docType, template: tpl }),
        });

        if (!response.ok) {
          const payload = (await response.json().catch(() => null)) as {
            message?: string;
            details?: Record<string, string>;
          } | null;
          setPreviewError(payload?.message ?? 'Die Vorschau konnte nicht erzeugt werden.');
          if (payload?.details) setErrors(payload.details);
          return;
        }

        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = url;
        setPreviewUrl(url);
        setPreviewError(null);
        setErrors({});
      } catch {
        setPreviewError('Der Server ist nicht erreichbar.');
      }
    }, 500);

    return () => clearTimeout(handle);
  }, [tpl, docType]);

  useEffect(
    () => () => {
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    },
    [],
  );

  // --- Speichern und Zuruecksetzen ------------------------------------------
  const save = useMutation({
    mutationFn: (template: DocTemplate) =>
      api.put<{ template: DocTemplate; isCustom: boolean }>(
        `/api/settings/templates/${docType}`,
        template,
      ),
    onSuccess: (result) => {
      setIsCustom(result.isCustom);
      queryClient.invalidateQueries({ queryKey: ['doc-template', docType] });
      toast.success('Die Vorlage wurde gespeichert.');
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        setErrors(error.fieldErrors);
        toast.error(error.message);
      } else {
        toast.error('Beim Speichern ist ein Fehler aufgetreten.');
      }
    },
  });

  const reset = useMutation({
    mutationFn: () =>
      api.delete<{ template: DocTemplate; isCustom: boolean }>(`/api/settings/templates/${docType}`),
    onSuccess: (result) => {
      setTpl(result.template);
      setIsCustom(false);
      queryClient.invalidateQueries({ queryKey: ['doc-template', docType] });
      toast.success('Die Vorlage wurde auf den Standard zurückgesetzt.');
    },
    onError: () => toast.error('Zurücksetzen fehlgeschlagen.'),
  });

  if (isLoading || !tpl) return <LoadingBlock />;

  /** Einen Teilblock der Vorlage ändern, z. B. patch('logo', { show: true }). */
  function patch<K extends keyof DocTemplate>(key: K, value: Partial<DocTemplate[K]>) {
    setTpl((current) =>
      current ? { ...current, [key]: { ...(current[key] as object), ...value } } : current,
    );
  }

  function patchExtraText(index: number, value: Partial<DocTemplateExtraText>) {
    setTpl((current) => {
      if (!current) return current;
      const extraTexts = current.extraTexts.map((entry, i) =>
        i === index ? { ...entry, ...value } : entry,
      );
      return { ...current, extraTexts };
    });
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(340px, 1fr) minmax(320px, 1fr)', gap: 16, alignItems: 'start' }}>
      <div className="card">
        <div className="card-header">
          <h2>Belegdesign</h2>
          <div className="row" style={{ gap: 6 }}>
            {!readOnly && isCustom ? (
              <button
                type="button"
                className="btn btn-sm"
                disabled={reset.isPending}
                onClick={() => reset.mutate()}
              >
                Auf Standard zurücksetzen
              </button>
            ) : null}
            {!readOnly ? (
              <button
                type="button"
                className="btn btn-primary btn-sm"
                disabled={save.isPending}
                onClick={() => save.mutate(tpl)}
              >
                Speichern
              </button>
            ) : null}
          </div>
        </div>

        <Field label="Belegart">
          <Select value={docType} onChange={(value) => setDocType(value)} options={TYPE_OPTIONS} />
        </Field>

        <Alert kind="info">
          Alle Maße in Millimetern auf der A4-Seite. Pflichtblöcke wie Anschrift, Belegdaten und
          Fußzeile lassen sich verschieben, aber nicht entfernen. Änderungen wirken nur auf künftig
          erzeugte PDFs — festgeschriebene Belege bleiben unverändert im Archiv.
        </Alert>

        <fieldset style={{ marginBottom: 14 }}>
          <legend>Seite</legend>
          <div className="row" style={{ flexWrap: 'wrap', gap: 8 }}>
            <Num label="Rand links" value={tpl.page.marginLeftMm} onChange={(v) => patch('page', { marginLeftMm: v })} error={errors['page.marginLeftMm']} disabled={readOnly} />
            <Num label="Rand rechts" value={tpl.page.marginRightMm} onChange={(v) => patch('page', { marginRightMm: v })} error={errors['page.marginRightMm']} disabled={readOnly} />
            <Num label="Rand oben" value={tpl.page.marginTopMm} onChange={(v) => patch('page', { marginTopMm: v })} error={errors['page.marginTopMm']} disabled={readOnly} />
            <Num label="Rand unten" value={tpl.page.marginBottomMm} onChange={(v) => patch('page', { marginBottomMm: v })} error={errors['page.marginBottomMm']} disabled={readOnly} />
            <Num label="Inhalt ab" value={tpl.page.contentTopMm} onChange={(v) => patch('page', { contentTopMm: v })} error={errors['page.contentTopMm']} disabled={readOnly} />
          </div>
        </fieldset>

        <fieldset style={{ marginBottom: 14 }}>
          <legend>Farben</legend>
          <div className="row" style={{ flexWrap: 'wrap', gap: 8 }}>
            <ColorField label="Text" value={tpl.colors.text} onChange={(v) => patch('colors', { text: v })} disabled={readOnly} />
            <ColorField label="Nebentext" value={tpl.colors.muted} onChange={(v) => patch('colors', { muted: v })} disabled={readOnly} />
            <ColorField label="Linien" value={tpl.colors.line} onChange={(v) => patch('colors', { line: v })} disabled={readOnly} />
            <ColorField label="Tabellenkopf" value={tpl.colors.tableHeaderBackground} onChange={(v) => patch('colors', { tableHeaderBackground: v })} disabled={readOnly} />
          </div>
        </fieldset>

        <fieldset style={{ marginBottom: 14 }}>
          <legend>Logo</legend>
          <label className="checkbox-row" style={{ marginBottom: 6 }}>
            <input type="checkbox" checked={tpl.logo.show} disabled={readOnly} onChange={(e) => patch('logo', { show: e.target.checked })} />
            <span>Logo aus den Firmendaten drucken</span>
          </label>
          {tpl.logo.show ? (
            <div className="row" style={{ flexWrap: 'wrap', gap: 8 }}>
              <Num label="X" value={tpl.logo.xMm} onChange={(v) => patch('logo', { xMm: v })} error={errors['logo.xMm']} disabled={readOnly} />
              <Num label="Y" value={tpl.logo.yMm} onChange={(v) => patch('logo', { yMm: v })} error={errors['logo.yMm']} disabled={readOnly} />
              <Num label="Breite" value={tpl.logo.widthMm} onChange={(v) => patch('logo', { widthMm: v })} error={errors['logo.widthMm']} disabled={readOnly} />
            </div>
          ) : null}
        </fieldset>

        <fieldset style={{ marginBottom: 14 }}>
          <legend>Firmenname im Briefkopf</legend>
          <label className="checkbox-row" style={{ marginBottom: 6 }}>
            <input type="checkbox" checked={tpl.companyName.show} disabled={readOnly} onChange={(e) => patch('companyName', { show: e.target.checked })} />
            <span>Firmennamen als Schriftzug drucken</span>
          </label>
          {tpl.companyName.show ? (
            <div className="row" style={{ flexWrap: 'wrap', gap: 8 }}>
              <Num label="X" value={tpl.companyName.xMm} onChange={(v) => patch('companyName', { xMm: v })} error={errors['companyName.xMm']} disabled={readOnly} />
              <Num label="Y" value={tpl.companyName.yMm} onChange={(v) => patch('companyName', { yMm: v })} error={errors['companyName.yMm']} disabled={readOnly} />
              <Num label="Breite" value={tpl.companyName.widthMm} onChange={(v) => patch('companyName', { widthMm: v })} error={errors['companyName.widthMm']} disabled={readOnly} />
              <Num label="Schrift (pt)" value={tpl.companyName.fontSizePt} onChange={(v) => patch('companyName', { fontSizePt: v })} error={errors['companyName.fontSizePt']} disabled={readOnly} />
              <AlignField value={tpl.companyName.align} onChange={(v) => patch('companyName', { align: v })} disabled={readOnly} />
            </div>
          ) : null}
        </fieldset>

        <fieldset style={{ marginBottom: 14 }}>
          <legend>Absenderzeile (Fensterumschlag)</legend>
          <label className="checkbox-row" style={{ marginBottom: 6 }}>
            <input type="checkbox" checked={tpl.senderLine.show} disabled={readOnly} onChange={(e) => patch('senderLine', { show: e.target.checked })} />
            <span>Absenderzeile über dem Anschriftfeld drucken</span>
          </label>
          {tpl.senderLine.show ? (
            <div className="row" style={{ flexWrap: 'wrap', gap: 8 }}>
              <Num label="X" value={tpl.senderLine.xMm} onChange={(v) => patch('senderLine', { xMm: v })} error={errors['senderLine.xMm']} disabled={readOnly} />
              <Num label="Y" value={tpl.senderLine.yMm} onChange={(v) => patch('senderLine', { yMm: v })} error={errors['senderLine.yMm']} disabled={readOnly} />
              <Num label="Breite" value={tpl.senderLine.widthMm} onChange={(v) => patch('senderLine', { widthMm: v })} error={errors['senderLine.widthMm']} disabled={readOnly} />
            </div>
          ) : null}
        </fieldset>

        <fieldset style={{ marginBottom: 14 }}>
          <legend>Anschriftfeld (Pflichtblock)</legend>
          <div className="row" style={{ flexWrap: 'wrap', gap: 8 }}>
            <Num label="X" value={tpl.addressField.xMm} onChange={(v) => patch('addressField', { xMm: v })} error={errors['addressField.xMm']} disabled={readOnly} />
            <Num label="Y" value={tpl.addressField.yMm} onChange={(v) => patch('addressField', { yMm: v })} error={errors['addressField.yMm']} disabled={readOnly} />
            <Num label="Breite" value={tpl.addressField.widthMm} onChange={(v) => patch('addressField', { widthMm: v })} error={errors['addressField.widthMm']} disabled={readOnly} />
          </div>
        </fieldset>

        <fieldset style={{ marginBottom: 14 }}>
          <legend>Belegdaten-Block (Pflichtblock)</legend>
          <div className="row" style={{ flexWrap: 'wrap', gap: 8 }}>
            <Num label="X" value={tpl.infoBlock.xMm} onChange={(v) => patch('infoBlock', { xMm: v })} error={errors['infoBlock.xMm']} disabled={readOnly} />
            <Num label="Y" value={tpl.infoBlock.yMm} onChange={(v) => patch('infoBlock', { yMm: v })} error={errors['infoBlock.yMm']} disabled={readOnly} />
            <Num label="Breite" value={tpl.infoBlock.widthMm} onChange={(v) => patch('infoBlock', { widthMm: v })} error={errors['infoBlock.widthMm']} disabled={readOnly} />
            <Num label="Beschriftung" value={tpl.infoBlock.labelWidthMm} onChange={(v) => patch('infoBlock', { labelWidthMm: v })} error={errors['infoBlock.labelWidthMm']} disabled={readOnly} />
          </div>
        </fieldset>

        <fieldset style={{ marginBottom: 14 }}>
          <legend>Positionstabelle</legend>
          <label className="checkbox-row">
            <input type="checkbox" checked={tpl.table.showPosition} disabled={readOnly} onChange={(e) => patch('table', { showPosition: e.target.checked })} />
            <span>Spalte „Pos"</span>
          </label>
          <label className="checkbox-row">
            <input type="checkbox" checked={tpl.table.showItemNumber} disabled={readOnly} onChange={(e) => patch('table', { showItemNumber: e.target.checked })} />
            <span>Artikelnummer unter der Bezeichnung</span>
          </label>
          <label className="checkbox-row">
            <input type="checkbox" checked={tpl.table.showDiscount} disabled={readOnly} onChange={(e) => patch('table', { showDiscount: e.target.checked })} />
            <span>Rabattspalte</span>
          </label>
        </fieldset>

        <fieldset style={{ marginBottom: 14 }}>
          <legend>Fußzeile (Pflichtblock)</legend>
          <div className="row" style={{ flexWrap: 'wrap', gap: 8, alignItems: 'flex-end' }}>
            <Field label="Stellung">
              <Select
                value={tpl.footer.orientation}
                onChange={(value) => patch('footer', { orientation: value })}
                options={[
                  { value: 'bottom', label: 'Unten (klassisch)' },
                  { value: 'left', label: 'Vertikal am linken Rand' },
                ]}
                disabled={readOnly}
              />
            </Field>
            <Num label="Schrift (pt)" value={tpl.footer.fontSizePt} onChange={(v) => patch('footer', { fontSizePt: v })} error={errors['footer.fontSizePt']} disabled={readOnly} />
          </div>
        </fieldset>

        <fieldset>
          <legend style={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}>
            <span>Zusatztexte</span>
            {!readOnly ? (
              <button
                type="button"
                className="btn btn-quiet btn-sm"
                onClick={() =>
                  setTpl((current) =>
                    current
                      ? {
                          ...current,
                          extraTexts: [
                            ...current.extraTexts,
                            { text: 'Neuer Text', xMm: 25, yMm: 30, widthMm: 80, fontSizePt: 9, align: 'left', bold: false, rotate: 0 },
                          ],
                        }
                      : current,
                  )
                }
              >
                Text hinzufügen
              </button>
            ) : null}
          </legend>

          {tpl.extraTexts.length === 0 ? (
            <div className="faint" style={{ fontSize: 13 }}>
              Frei platzierbare Texte, z. B. eine Zertifikatsnummer oder ein Hinweis am Rand.
            </div>
          ) : null}

          {tpl.extraTexts.map((entry, index) => (
            <div key={index} style={{ border: '1px solid var(--border, #d5d9e0)', borderRadius: 6, padding: 8, marginBottom: 8 }}>
              <Field label={`Text ${index + 1}`} error={errors[`extraTexts.${index}.text`]}>
                <input autoComplete="off"
                  className="input"
                  value={entry.text}
                  disabled={readOnly}
                  onChange={(e) => patchExtraText(index, { text: e.target.value })}
                />
              </Field>
              <div className="row" style={{ flexWrap: 'wrap', gap: 8, alignItems: 'flex-end' }}>
                <Num label="X" value={entry.xMm} onChange={(v) => patchExtraText(index, { xMm: v })} error={errors[`extraTexts.${index}.xMm`]} disabled={readOnly} />
                <Num label="Y" value={entry.yMm} onChange={(v) => patchExtraText(index, { yMm: v })} error={errors[`extraTexts.${index}.yMm`]} disabled={readOnly} />
                <Num label="Breite" value={entry.widthMm} onChange={(v) => patchExtraText(index, { widthMm: v })} error={errors[`extraTexts.${index}.widthMm`]} disabled={readOnly} />
                <Num label="Schrift (pt)" value={entry.fontSizePt} onChange={(v) => patchExtraText(index, { fontSizePt: v })} error={errors[`extraTexts.${index}.fontSizePt`]} disabled={readOnly} />
                <AlignField value={entry.align} onChange={(v) => patchExtraText(index, { align: v })} disabled={readOnly} />
                <Field label="Drehung">
                  <Select
                    value={String(entry.rotate) as '0' | '90'}
                    onChange={(value) => patchExtraText(index, { rotate: Number(value) as 0 | 90 })}
                    options={[
                      { value: '0', label: 'Waagerecht' },
                      { value: '90', label: 'Senkrecht' },
                    ]}
                    disabled={readOnly}
                  />
                </Field>
                <label className="checkbox-row" style={{ paddingBottom: 8 }}>
                  <input type="checkbox" checked={entry.bold} disabled={readOnly} onChange={(e) => patchExtraText(index, { bold: e.target.checked })} />
                  <span>Fett</span>
                </label>
                {!readOnly ? (
                  <button
                    type="button"
                    className="btn btn-quiet btn-sm"
                    style={{ marginBottom: 8 }}
                    onClick={() =>
                      setTpl((current) =>
                        current
                          ? { ...current, extraTexts: current.extraTexts.filter((_, i) => i !== index) }
                          : current,
                      )
                    }
                  >
                    Entfernen
                  </button>
                ) : null}
              </div>
            </div>
          ))}

          <details style={{ marginTop: 6 }}>
            <summary className="faint" style={{ cursor: 'pointer', fontSize: 13 }}>
              Verfügbare Platzhalter
            </summary>
            <div className="faint" style={{ fontSize: 12.5, marginTop: 4, lineHeight: 1.6 }}>
              {TEMPLATE_FIELDS.map((field) => (
                <div key={field.key}>
                  <code>{`{${field.key}}`}</code> — {field.label}
                </div>
              ))}
            </div>
          </details>
        </fieldset>
      </div>

      <div className="card" style={{ position: 'sticky', top: 12 }}>
        <div className="card-header">
          <h2>Vorschau</h2>
          {isCustom ? <span className="faint" style={{ fontSize: 12.5 }}>eigene Vorlage</span> : <span className="faint" style={{ fontSize: 12.5 }}>Standard</span>}
        </div>
        {previewError ? <Alert kind="error">{previewError}</Alert> : null}
        {previewUrl ? (
          <iframe
            title="Belegvorschau"
            src={previewUrl}
            style={{ width: '100%', height: '75vh', border: '1px solid var(--border, #d5d9e0)', borderRadius: 6 }}
          />
        ) : (
          <LoadingBlock text="Vorschau wird erzeugt …" />
        )}
      </div>
    </div>
  );
}

function Num({
  label,
  value,
  onChange,
  error,
  disabled,
}: {
  label: string;
  value: number;
  onChange: (value: number) => void;
  error?: string;
  disabled?: boolean;
}) {
  return (
    <Field label={label} error={error}>
      <input autoComplete="off"
        className={`input${error ? ' invalid' : ''}`}
        type="number"
        style={{ width: 92 }}
        value={value}
        disabled={disabled}
        onChange={(event) => {
          const next = Number(event.target.value);
          if (Number.isFinite(next)) onChange(next);
        }}
      />
    </Field>
  );
}

function ColorField({
  label,
  value,
  onChange,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}) {
  return (
    <Field label={label}>
      <input
        type="color"
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        style={{ width: 60, height: 32, padding: 2, border: '1px solid var(--border, #d5d9e0)', borderRadius: 6, background: 'transparent' }}
      />
    </Field>
  );
}

function AlignField({
  value,
  onChange,
  disabled,
}: {
  value: 'left' | 'center' | 'right';
  onChange: (value: 'left' | 'center' | 'right') => void;
  disabled?: boolean;
}) {
  return (
    <Field label="Ausrichtung">
      <Select
        value={value}
        onChange={onChange}
        options={[
          { value: 'left', label: 'Links' },
          { value: 'center', label: 'Zentriert' },
          { value: 'right', label: 'Rechts' },
        ]}
        disabled={disabled}
      />
    </Field>
  );
}
